import {
  Box,
  Button,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Clock, Smartphone, X } from 'lucide-react';
import {
  scheduledLabel,
  scheduledText,
  type ScheduledView,
} from '../../lib/scheduled';
import { useT } from '../../i18n';
import { useLoadingState } from '../../hooks/useLoadingState';
import { TwindenLoader } from '../brand/TwindenLoader';

// "Scheduled · Tue 9:00" rows above the composer (M64, D239), for the
// sender only: the text opened on this device, Send now and Cancel. A row
// whose key changed says it needs one of my devices to seal it again.
export function ScheduledList({
  items,
  loading = false,
  onSendNow,
  onCancel,
}: {
  items: ScheduledView[];
  /**
   * The rows are still on their way (M66, D250). Pass it only once the
   * chat's messages are on screen, so it never joins the page mark.
   */
  loading?: boolean;
  onSendNow: (item: ScheduledView) => void;
  onCancel: (item: ScheduledView) => void;
}) {
  const t = useT();
  // A quiet extra (D250): a small mark above the composer only when the
  // server is slow to answer (after 150 ms, D248), never a status of its
  // own, and only while nothing else is loading. Most chats have no rows.
  const waiting = useLoadingState({ loading, hasData: items.length > 0 });
  if (items.length === 0) {
    if (!waiting.loader) return null;
    return (
      <HStack
        justify="center"
        py={1}
        mb={2}
        data-testid="scheduled-loading"
        aria-hidden="true"
      >
        <TwindenLoader size="inline" announce={false} />
      </HStack>
    );
  }
  return (
    <VStack
      as="section"
      aria-label={t('chat.scheduled.list')}
      spacing="6px"
      align="stretch"
      mb={2}
      maxH="168px"
      overflowY="auto"
    >
      {items.map((item) => {
        const text = scheduledText(item);
        // Only a row this device can't open needs another device (§7); one
        // it can open is being sealed again here, and Send now still works.
        const locked = item.needsReseal && text === null;
        return (
          <HStack
            key={item.id}
            data-testid="scheduled-row"
            bg="surface"
            borderWidth="1px"
            borderColor="line"
            borderLeftWidth="3px"
            borderLeftColor={locked ? 'saffron' : 'accent'}
            borderRadius="18px"
            pl={3}
            pr={1}
            py="6px"
            spacing={2}
          >
            <Box
              color={locked ? 'saffron.text' : 'accent'}
              flexShrink={0}
              aria-hidden="true"
            >
              {locked ? <Smartphone size={16} /> : <Clock size={16} />}
            </Box>
            <Box flex="1" minW={0}>
              <Text
                fontSize="13px"
                fontWeight={700}
                color={locked ? 'saffron.text' : 'accent'}
                noOfLines={1}
              >
                {locked
                  ? t('chat.scheduled.needsPhone')
                  : item.needsReseal
                    ? t('chat.scheduled.resealing')
                    : t('chat.scheduled.label', {
                        time: scheduledLabel(item.scheduledFor),
                      })}
              </Text>
              <Text fontSize="15px" noOfLines={1} color="ink.body">
                {text ?? t('chat.scheduled.locked')}
              </Text>
            </Box>
            {!locked && (
              <Button
                size="sm"
                variant="ghost"
                colorScheme="brand"
                flexShrink={0}
                onClick={() => onSendNow(item)}
              >
                {t('chat.scheduled.sendNow')}
              </Button>
            )}
            <IconButton
              aria-label={t('chat.scheduled.cancel')}
              icon={<X size={16} />}
              size="sm"
              variant="ghost"
              colorScheme="gray"
              flexShrink={0}
              onClick={() => onCancel(item)}
            />
          </HStack>
        );
      })}
    </VStack>
  );
}
